import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Breadcrumb, Button, Col, Form, Image, Input, Row, Select, Space } from 'antd';
import eiqSecureStore from '../../stores/eiq-secure.store';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { FormWrapper } from '../../../../common/components/form-wrapper/form-wrapper';
import './eiq-secure-resident-license.scss';
import guestService from '../../../../common/services/guest-service/guest.service';
import { notification } from '../../../../common/utils/notification';
import { formatAddressLabel } from 'common/helpers/address.helper';
import configurationStore from 'common/stores/configuration/configuration.store';
import { eiqSecureResidentLicenseValidation } from './eiq-secure-resident-license.validation';
import { entryTypeOptions, toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import { useTableContext } from '../../../../common/components/table/table-context';
import { vehicleInfoBlock } from '../../../../common/components/vehicle-info-block/vehicle-info-block';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import { useMedia } from '../../../../common/hooks/use-media/use-media.hook';
import Footer from '../../../../common/components/footer/footer';
import { setResidentListSearchFilter } from 'common/helpers/search-filter.helper';
import { VehicleStatusEnum } from 'common/enums/vehicle-status.enum';
import { EntryType } from 'common/enums/entry-type.enum';
import SelectState from 'common/components/form-elements/select-state/select-state';
import TextArea from 'antd/es/input/TextArea';
import featureStore from 'common/stores/features/feature.store';
import { useDefaultAdmitValues } from 'features/eiq-secure/hooks/use-default-admit-values/use-default-admit-values.hook';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';

interface IEiqSecureResidentLicenseProps {
  residentLicenseAdmit: boolean;
}

const isEntryType = (value: string): value is keyof typeof EntryType =>
  Object.prototype.hasOwnProperty.call(EntryType, value) && typeof EntryType[value as keyof typeof EntryType] === 'number';

const hasEditableVehicleDetails = (entryType: EntryType) => entryType === EntryType.Passenger || entryType === EntryType.UnlistedVehicle;

export const EiqSecureResidentLicense = observer(({ residentLicenseAdmit }: IEiqSecureResidentLicenseProps) => {
  const tableContext = useTableContext();
  const { isMobileOrTable } = useMedia();
  const [form] = Form.useForm();

  const configuredEntryType = configurationStore.configuration?.default.entryType;
  const defaultEntryType = configuredEntryType && isEntryType(configuredEntryType) ? EntryType[configuredEntryType] : EntryType.Driver;
  const watchedEntryType = Form.useWatch('entryType', form);
  const entryType = watchedEntryType ?? defaultEntryType;

  const gateOptions = useMemo(() => toGateOptions(configurationStore?.configuration?.gates || []), []);

  const vehicles = useMemo(
    () =>
      eiqSecureStore.residentDetails?.addresses.find((addressItem) => addressItem?.address?.id === eiqSecureStore.selectedAddressId)
        ?.vehicles,
    [eiqSecureStore.selectedAddressId, eiqSecureStore.residentDetails],
  );

  const { defaultGate, defaultLicenseState, defaultLicensePlate, image, camera } = useDefaultAdmitValues({
    currentGuest: null,
  });

  useBoundPlateRecognition(() => {
    const currentEntryType = form.getFieldValue('entryType');

    if (hasEditableVehicleDetails(currentEntryType)) {
      form.setFieldValue('carLicensePlate', '');
      form.setFieldValue('carState', configurationStore.configuration?.default.defaultLicenseState);
    }

    form.setFieldValue('gate', userAuthStore.defaultGate || undefined);
  });

  const onFinish = (values: any) => {
    const { vehicleId, gate, carLicensePlate, carState, entryType: submittedEntryType, note } = values;
    const vehicle = vehicles?.find((vehicleItem) => vehicleItem.id === vehicleId);

    if (submittedEntryType === EntryType.Driver && !vehicle) {
      return;
    }

    const usesRegisteredVehicle = submittedEntryType === EntryType.Driver;
    const includesDriverDetails = hasEditableVehicleDetails(submittedEntryType);
    const admitCarState = usesRegisteredVehicle ? vehicle?.licenseState : carState;
    const admitCarLicensePlate = usesRegisteredVehicle ? vehicle?.licenseNumber : carLicensePlate;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(gate, admitCarLicensePlate, admitCarState);

    const admitResidentBody = {
      entryType: submittedEntryType,
      gate,
      carState: admitCarState,
      carLicensePlate: admitCarLicensePlate,
      carExpirationDate: vehicle?.validTo,
      deviceId: vehicle?.deviceId,
      driverCompanyName: submittedEntryType === EntryType.Passenger ? values.driverCompanyName : null,
      driverName: includesDriverDetails ? `${values.driverFirstName ?? ''} ${values.driverLastName ?? ''}`.trim() : null,
      note: submittedEntryType === EntryType.Other ? note : null,
      image: includesDriverDetails ? image : undefined,
      camera: includesDriverDetails ? camera : undefined,
      plateRecognitionId,
    };

    const residentId = eiqSecureStore?.residentDetails?.id;
    const selectedAddressId = eiqSecureStore?.selectedAddressId;

    if (!residentId || !selectedAddressId) {
      return;
    }

    return guestService.admitResident(residentId, selectedAddressId, admitResidentBody).then(() => {
      notification.success({ message: 'The resident  has been admitted' });
      tableContext.setSelectedRow(null);
      eiqSecureStore.setAdmitFlow(null);
      eiqSecureStore.setIsAdmitFlowProcessed();
      plateRecognitionContext.clear();
      setResidentListSearchFilter('');
    });
  };

  const handleClickAdmit = () => {
    form.submit();
  };

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  useEffect(() => {
    if (residentLicenseAdmit) {
      handleClickAdmit();
    }
  }, [residentLicenseAdmit]);

  const licenseOptions = useMemo(
    () =>
      vehicles?.map((vehicle) => ({
        label: vehicleInfoBlock(vehicle),
        value: vehicle.id,
        isApproved: vehicle.status === VehicleStatusEnum.Approved,
      })),
    [eiqSecureStore.selectedAddressId, eiqSecureStore.residentDetails],
  );

  const showEditableVehicleDetails = hasEditableVehicleDetails(entryType);
  const showPlateScan = featureStore.isPlateScanEnabled && image && showEditableVehicleDetails;
  const defaultVehicleId =
    entryType === EntryType.Driver ? licenseOptions?.find((option) => option.isApproved)?.value || licenseOptions?.[0]?.value : undefined;

  const onEntryTypeChanged = (newEntryType: EntryType) => {
    if (newEntryType === EntryType.Passenger) {
      form.setFieldValue('carLicensePlate', defaultLicensePlate);
      form.setFieldValue('carState', defaultLicenseState);
      form.setFieldValue('driverFirstName', '');
      form.setFieldValue('driverLastName', '');
    }

    if (newEntryType === EntryType.Driver) {
      const primaryLicensePlate = eiqSecureStore.selectedGuest?.carLicensePlates.find((licensePlate) => licensePlate.isPrimary);

      form.setFieldValue('carLicensePlate', primaryLicensePlate?.number);
      form.setFieldValue('carState', primaryLicensePlate?.state ?? defaultLicenseState);
    }

    if (newEntryType === EntryType.UnlistedVehicle) {
      form.setFieldValue('carLicensePlate', defaultLicensePlate);
      form.setFieldValue('carState', defaultLicenseState);
      form.setFieldValue('driverFirstName', eiqSecureStore.selectedResident?.firstName);
      form.setFieldValue('driverLastName', eiqSecureStore.selectedResident?.lastName);
    }
  };

  const admitActionButton = (
    <Button type="primary" className="primary-btn" icon={<AdmitIcon />} onClick={handleClickAdmit}>
      Approve
    </Button>
  );

  return (
    <div className="resident-license">
      <Row justify="space-between">
        <Col>
          <div className="space-12">
            <div className="breadcrumb-wrapper">
              <Button icon={<BackIcon />} onClick={() => eiqSecureStore.setAdmitFlow(null)}>
                Back
              </Button>
              <Breadcrumb separator="–" items={[{ title: 'License' }]} />
            </div>
            <span className="sub-title">{formatAddressLabel(eiqSecureStore.getSelectedAddress())}</span>
          </div>
        </Col>
        <Col>
          <Space size={8}>{!isMobileOrTable && admitActionButton}</Space>
        </Col>
      </Row>

      <div className="container-row">
        <Row gutter={32}>
          {showPlateScan && (
            <Col lg={8} sm={24} className="instructions-wrapper">
              <div className="instructions-item">
                <span className="title">PlateScan ({camera})</span>
                <Image src={image} alt="License Plate" loading="lazy" />
              </div>
            </Col>
          )}

          <Col xs={24} lg={12}>
            <FormWrapper form={form} onFinish={onFinish} layout="vertical">
              <Form.Item name="entryType" label="Entry Type" initialValue={defaultEntryType}>
                <Select
                  options={entryTypeOptions}
                  placeholder="Select Entry Type"
                  popupClassName="so-select"
                  onChange={onEntryTypeChanged}
                />
              </Form.Item>

              {showEditableVehicleDetails && (
                <>
                  <Form.Item
                    name="driverFirstName"
                    label="Driver first name"
                    rules={entryType === EntryType.UnlistedVehicle ? eiqSecureResidentLicenseValidation.driverFirstName : []}>
                    <Input placeholder="Enter driver first name" autoFocus />
                  </Form.Item>
                  <Form.Item
                    name="driverLastName"
                    label="Driver last name"
                    rules={entryType === EntryType.UnlistedVehicle ? eiqSecureResidentLicenseValidation.driverLastName : []}>
                    <Input placeholder="Enter driver last name" />
                  </Form.Item>
                </>
              )}

              {entryType === EntryType.Passenger && (
                <Form.Item
                  name="driverCompanyName"
                  label="Driver company name"
                  rules={eiqSecureResidentLicenseValidation.driverCompanyName}>
                  <Input placeholder="Enter driver company name" />
                </Form.Item>
              )}

              {entryType === EntryType.Driver && (
                <Form.Item
                  name="vehicleId"
                  label="License"
                  initialValue={defaultVehicleId}
                  rules={eiqSecureResidentLicenseValidation.carLicensePlate}>
                  <Select options={licenseOptions} placeholder="Select License" popupClassName="so-select" />
                </Form.Item>
              )}

              {showEditableVehicleDetails && (
                <>
                  <Form.Item name="carLicensePlate" label="License Plate" rules={eiqSecureResidentLicenseValidation.carLicensePlate}>
                    <Input placeholder="Enter license plate" />
                  </Form.Item>

                  <Form.Item
                    name="carState"
                    label="License state"
                    initialValue={defaultLicenseState}
                    rules={eiqSecureResidentLicenseValidation.carState}>
                    <SelectState placeholder="Select a license state" popupClassName="so-select" />
                  </Form.Item>
                </>
              )}

              {entryType === EntryType.Other && (
                <Form.Item name="note" label="Notes">
                  <TextArea placeholder="Enter notes" maxLength={500} style={{ resize: 'none' }} autoFocus />
                </Form.Item>
              )}

              <Form.Item name="gate" label="Location (Gate)" rules={eiqSecureResidentLicenseValidation.gate} initialValue={defaultGate}>
                <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
              </Form.Item>
            </FormWrapper>
          </Col>
        </Row>
      </div>

      {isMobileOrTable && <Footer>{admitActionButton}</Footer>}
    </div>
  );
});
